/**
 * HTTP Client
 *
 * Client HTTP centralizzato con gestione errori, retry e logging.
 * Supporta sia chiamate API reali che mock data.
 */

import { API_CONFIG, STORAGE_KEYS } from '@/config/constants';
import { env, isDev } from '@/config/env';
import type { ApiErrorResponse } from './types';

// =============================================================================
// Error Handling
// =============================================================================

/**
 * Errore API personalizzato con informazioni strutturate
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string[]>;

  constructor(
    message: string,
    status: number,
    code: string,
    details?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Errore di rete (no response) */
  static isNetworkError(error: unknown): boolean {
    return error instanceof TypeError && error.message === 'Failed to fetch';
  }

  /** Errore di autenticazione (401) */
  isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Errore di permessi (403) */
  isForbidden(): boolean {
    return this.status === 403;
  }

  /** Risorsa non trovata (404) */
  isNotFound(): boolean {
    return this.status === 404;
  }

  /** Errore di validazione (422) */
  isValidationError(): boolean {
    return this.status === 422;
  }

  /** Errore server (5xx) */
  isServerError(): boolean {
    return this.status >= 500;
  }

  /** Rate limited (429) */
  isRateLimited(): boolean {
    return this.status === 429;
  }
}

// =============================================================================
// Request Configuration
// =============================================================================

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestConfig {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: unknown;
  timeout?: number;
  signal?: AbortSignal;
  /**
   * Numero di retry automatici. Default `API_CONFIG.MAX_RETRIES`.
   * Impostare a 0 per operazioni con effetti collaterali non ripetibili
   * (es. invio email): un retry su timeout restituirebbe l'esito del
   * secondo run, mascherando quello reale.
   */
  retries?: number;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Delay helper per retry
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Calcola delay con exponential backoff
 */
function getRetryDelay(attempt: number): number {
  return API_CONFIG.RETRY_DELAY * Math.pow(2, attempt);
}

/**
 * Calcola delay rispettando Retry-After header
 */
function getRetryDelayFromResponse(response: Response, attempt: number): number {
  const retryAfter = response.headers.get('Retry-After');

  if (retryAfter) {
    const retryAfterNum = parseInt(retryAfter, 10);

    if (!isNaN(retryAfterNum)) {
      // È un numero di secondi
      return retryAfterNum * 1000;
    }

    // Prova a parsare come data
    const retryDate = new Date(retryAfter);
    if (!isNaN(retryDate.getTime())) {
      return Math.max(0, retryDate.getTime() - Date.now());
    }
  }

  // Fallback a exponential backoff
  return getRetryDelay(attempt);
}

/**
 * Verifica se un errore è retryable
 */
function isRetryableError(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

/**
 * Recupera il token di autenticazione
 */
function getAuthToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

// =============================================================================
// 401 Unauthorized Handler
// =============================================================================

/**
 * Callback per gestire il logout su 401 Unauthorized.
 * Viene impostato da AuthContext per evitare dipendenze circolari.
 */
let onUnauthorizedCallback: (() => void) | null = null;

/**
 * Registra il callback da eseguire su errore 401.
 * Chiamato da AuthContext al mount.
 */
export function setOnUnauthorizedCallback(callback: () => void): void {
  onUnauthorizedCallback = callback;
}

/**
 * Rimuove il callback 401.
 * Chiamato da AuthContext al unmount.
 */
export function clearOnUnauthorizedCallback(): void {
  onUnauthorizedCallback = null;
}

/**
 * Gestisce errore 401: pulisce token e invoca callback logout.
 * Esclude endpoint di auth per evitare loop infiniti.
 */
function handleUnauthorized(url: string): void {
  // Non fare logout automatico per endpoint di auth (evita loop)
  if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
    return;
  }

  if (isDev) {
    console.warn('[API] 401 Unauthorized - triggering auto-logout');
  }

  // Pulisci token localmente
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);

  // Invoca callback se registrato
  if (onUnauthorizedCallback) {
    onUnauthorizedCallback();
  }
}

/**
 * Verifica se il Content-Type è JSON
 */
function isJsonContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  return contentType.includes('application/json');
}

// =============================================================================
// HTTP Client Implementation
// =============================================================================

/**
 * Esegue una richiesta HTTP con gestione errori e retry
 * @returns Promise<T | undefined> - undefined per 204 No Content
 */
async function request<T>(url: string, config: RequestConfig = {}): Promise<T | undefined> {
  const { method = 'GET', headers = {}, body, timeout = API_CONFIG.TIMEOUT } = config;

  // Costruisci headers
  const requestHeaders: Record<string, string> = {
    ...API_CONFIG.DEFAULT_HEADERS,
    ...headers,
  };

  // Aggiungi token se presente
  const token = getAuthToken();
  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Crea AbortController per timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      signal: config.signal || controller.signal,
    });

    clearTimeout(timeoutId);

    // Gestisci errori HTTP
    if (!response.ok) {
      let errorData: ApiErrorResponse | null = null;
      let rawErrorBody = '';

      try {
        const responseText = await response.text();
        rawErrorBody = responseText;
        errorData = JSON.parse(responseText);
      } catch (parseError) {
        // Response non JSON - log in dev
        if (isDev) {
          console.error('[API] Failed to parse error response:', {
            status: response.status,
            contentType: response.headers.get('Content-Type'),
            body: rawErrorBody.substring(0, 500),
            error: parseError instanceof Error ? parseError.message : 'Unknown error',
          });
        }
      }

      // Gestisce sia formato NestJS { message, error, statusCode }
      // che formato custom { error: { message, code } }
      const errorMessage =
        errorData?.message ||
        errorData?.error?.message ||
        `HTTP Error ${response.status}`;

      const apiError = new ApiError(
        errorMessage,
        response.status,
        errorData?.error?.code || 'UNKNOWN_ERROR',
        errorData?.error?.details
      );

      // Gestisci 401 Unauthorized con auto-logout
      if (apiError.isUnauthorized()) {
        handleUnauthorized(url);
      }

      throw apiError;
    }

    // Gestisci response vuota (204 No Content)
    if (response.status === 204) {
      return undefined;
    }

    // Verifica Content-Type prima di parsare JSON
    const contentType = response.headers.get('Content-Type');
    if (!isJsonContentType(contentType)) {
      // Se non è JSON ma ci aspettiamo una risposta, logga un warning
      if (isDev) {
        console.warn(
          `[API] Expected JSON response but got ${contentType || 'unknown'} for ${url}`
        );
      }
      // Tenta comunque di parsare (potrebbe essere JSON senza header corretto)
    }

    try {
      return await response.json();
    } catch (parseError) {
      throw new ApiError(
        'Failed to parse JSON response',
        response.status,
        'JSON_PARSE_ERROR'
      );
    }
  } catch (error) {
    clearTimeout(timeoutId);

    // Timeout
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError('Request timeout', 408, 'TIMEOUT');
    }

    // Network error
    if (ApiError.isNetworkError(error)) {
      throw new ApiError('Network error', 0, 'NETWORK_ERROR');
    }

    // Re-throw ApiError
    if (error instanceof ApiError) {
      throw error;
    }

    // Unknown error
    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error',
      0,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Esegue una richiesta con retry automatico
 */
async function requestWithRetry<T>(
  url: string,
  config: RequestConfig = {},
  maxRetries: number = API_CONFIG.MAX_RETRIES
): Promise<T | undefined> {
  let lastError: Error | null = null;
  let lastResponse: Response | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await request<T>(url, config);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Non fare retry per errori non retryable
      if (error instanceof ApiError && !isRetryableError(error.status)) {
        throw error;
      }

      // Log retry in dev
      if (isDev && attempt < maxRetries) {
        console.warn(`[API] Retry ${attempt + 1}/${maxRetries} for ${url}`);
      }

      // Calcola delay (rispetta Retry-After se presente)
      const retryDelayMs = lastResponse
        ? getRetryDelayFromResponse(lastResponse, attempt)
        : getRetryDelay(attempt);

      // Attendi prima del prossimo tentativo
      if (attempt < maxRetries) {
        await delay(retryDelayMs);
      }
    }
  }

  throw lastError;
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Client HTTP per le API
 *
 * @example
 * ```typescript
 * // GET request
 * const trips = await httpClient.get<Trip[]>('/trips');
 *
 * // POST request
 * const newTrip = await httpClient.post<Trip>('/trips', { name: 'New Trip' });
 *
 * // DELETE request (returns void)
 * await httpClient.delete<void>('/trips/123');
 * ```
 */
export const httpClient = {
  /**
   * Richiesta GET
   */
  get<T>(url: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    if (env.USE_MOCK_DATA && isDev) {
      console.log(`[Mock] GET ${url}`);
    }
    return requestWithRetry<T>(url, { ...config, method: 'GET' }) as Promise<T>;
  },

  /**
   * Richiesta POST
   */
  post<T>(url: string, body?: unknown, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    if (env.USE_MOCK_DATA && isDev) {
      console.log(`[Mock] POST ${url}`, body);
    }
    return requestWithRetry<T>(
      url,
      { ...config, method: 'POST', body },
      config?.retries
    ) as Promise<T>;
  },

  /**
   * Richiesta PUT
   */
  put<T>(url: string, body?: unknown, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    if (env.USE_MOCK_DATA && isDev) {
      console.log(`[Mock] PUT ${url}`, body);
    }
    return requestWithRetry<T>(url, { ...config, method: 'PUT', body }) as Promise<T>;
  },

  /**
   * Richiesta PATCH
   */
  patch<T>(url: string, body?: unknown, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    if (env.USE_MOCK_DATA && isDev) {
      console.log(`[Mock] PATCH ${url}`, body);
    }
    return requestWithRetry<T>(url, { ...config, method: 'PATCH', body }) as Promise<T>;
  },

  /**
   * Richiesta DELETE
   * @returns Promise<T> - Per DELETE che ritornano dati, o void per 204
   */
  delete<T = void>(url: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    if (env.USE_MOCK_DATA && isDev) {
      console.log(`[Mock] DELETE ${url}`);
    }
    return requestWithRetry<T>(url, { ...config, method: 'DELETE' }) as Promise<T>;
  },
};
